import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
};
const sectionTitle = {
  fontWeight: 700, fontSize: '0.9rem', color: '#374151',
  marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #f3f4f6',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const EMPTY = {
  vendor_type_id: '',
  company_name: '',
  contact_person: '',
  phone: '',
  alternate_phone: '',
  email: '',
  gstin: '',
  pan: '',
  msme: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  state_code: '',
  pincode: '',
  country: 'India',
  payment_terms_days: '',
  opening_balance: '',
  opening_balance_type: 'debit',
  status: 'active',
  notes: '',
};

/* ── Moved outside component to prevent remount on every keystroke ── */
const Field = ({ label, children }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const Input = ({ value, onChange, ...props }) => (
  <input style={inputStyle} value={value} onChange={onChange} {...props} />
);

const Select = ({ value, onChange, children }) => (
  <select style={{ ...inputStyle, background: '#fff', cursor: 'pointer' }} value={value} onChange={onChange}>
    {children}
  </select>
);

export default function AdminVendorForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [types, setTypes]     = useState([]);
  const [form, setForm]       = useState(EMPTY);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const fetchTypes = useCallback(async () => {
    const { data } = await API.get('/vendors/types');
    setTypes((data.types || []).filter(t => t.status === 'active'));
  }, []);

  const fetchVendor = useCallback(async () => {
    try {
      const { data } = await API.get(`/vendors/${id}`);
      const v = data.vendor;
      setForm({
        vendor_type_id:       v.vendor_type_id || '',
        company_name:         v.company_name || '',
        contact_person:       v.contact_person || '',
        phone:                v.phone || '',
        alternate_phone:      v.alternate_phone || '',
        email:                v.email || '',
        gstin:                v.gstin || '',
        pan:                  v.pan || '',
        msme:                 v.msme || '',
        address_line1:        v.address_line1 || '',
        address_line2:        v.address_line2 || '',
        city:                 v.city || '',
        state:                v.state || '',
        state_code:           v.state_code || '',
        pincode:              v.pincode || '',
        country:              v.country || 'India',
        payment_terms_days:   v.payment_terms_days ?? '',
        opening_balance:      v.opening_balance ?? '',
        opening_balance_type: v.opening_balance_type || 'debit',
        status:               v.status || 'active',
        notes:                v.notes || '',
      });
    } catch (err) {
      toast.error('Failed to load vendor');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);
  useEffect(() => { if (isEdit) fetchVendor(); }, [isEdit, fetchVendor]);

  const handleSave = async () => {
    if (!form.company_name.trim()) return toast.error('Company name is required');
    try {
      setSaving(true);
      const payload = {
        ...form,
        vendor_type_id:     form.vendor_type_id || null,
        payment_terms_days: form.payment_terms_days !== '' ? Number(form.payment_terms_days) : null,
        opening_balance:    form.opening_balance !== '' ? Number(form.opening_balance) : 0,
      };
      if (isEdit) {
        await API.put(`/vendors/${id}`, payload);
        toast.success('Vendor updated');
        navigate(`/admin/vendors/${id}`);
      } else {
        const { data } = await API.post('/vendors', payload);
        toast.success('Vendor created');
        navigate(`/admin/vendors/${data.vendor.id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(isEdit ? `/admin/vendors/${id}` : '/admin/vendors')} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}>
            <FiArrowLeft size={16} />
          </button>
          <h2 style={{ fontWeight: 700, fontSize: '1.3rem', margin: 0 }}>{isEdit ? 'Edit Vendor' : 'Add Vendor'}</h2>
        </div>
        <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiSave size={14} /> {saving ? 'Saving…' : 'Save Vendor'}
        </button>
      </div>

      {/* ── Section 1: Basic Info ── */}
      <div className="crm-card" style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>Basic Information</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Field label="Company Name *">
            <Input value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="e.g. ABC Paper Suppliers" />
          </Field>
          <Field label="Vendor Type">
            <Select value={form.vendor_type_id} onChange={e => set('vendor_type_id', e.target.value)}>
              <option value="">— Select Type —</option>
              {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </Field>
          <Field label="Contact Person">
            <Input value={form.contact_person} onChange={e => set('contact_person', e.target.value)} placeholder="Primary contact name" />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 98765 43210" />
          </Field>
          <Field label="Alternate Phone">
            <Input value={form.alternate_phone} onChange={e => set('alternate_phone', e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Email">
            <Input value={form.email} onChange={e => set('email', e.target.value)} type="email" placeholder="vendor@example.com" />
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="GSTIN">
            <Input
              value={form.gstin}
              onChange={e => {
                const val = e.target.value.toUpperCase();
                set('gstin', val);
                if (val.length >= 12) set('pan', val.slice(2, 12));
                else if (val.length < 3) set('pan', '');
              }}
              placeholder="22AAAAA0000A1Z5"
            />
          </Field>
          <Field label="PAN">
            <Input value={form.pan} onChange={e => set('pan', e.target.value.toUpperCase())} placeholder="AAAAA0000A" />
          </Field>
          <Field label="MSME">
            <Input value={form.msme} onChange={e => set('msme', e.target.value)} placeholder="UDYAM-XX-00-0000000" />
          </Field>
        </div>
      </div>

      {/* ── Section 2: Address ── */}
      <div className="crm-card" style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>Address</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Address Line 1">
            <Input value={form.address_line1} onChange={e => set('address_line1', e.target.value)} placeholder="Street / Building" />
          </Field>
          <Field label="Address Line 2">
            <Input value={form.address_line2} onChange={e => set('address_line2', e.target.value)} placeholder="Area / Landmark" />
          </Field>
          <Field label="City">
            <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="City" />
          </Field>
          <Field label="State">
            <select
              value={form.state_code}
              onChange={e => {
                const opt = e.target.options[e.target.selectedIndex];
                set('state_code', e.target.value);
                set('state', opt.text);
              }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit', background: '#fff', cursor: 'pointer' }}
            >
              <option value="">Select State</option>
              <option value="01">Jammu and Kashmir</option>
              <option value="02">Himachal Pradesh</option>
              <option value="03">Punjab</option>
              <option value="04">Chandigarh</option>
              <option value="05">Uttarakhand</option>
              <option value="06">Haryana</option>
              <option value="07">Delhi</option>
              <option value="08">Rajasthan</option>
              <option value="09">Uttar Pradesh</option>
              <option value="10">Bihar</option>
              <option value="11">Sikkim</option>
              <option value="12">Arunachal Pradesh</option>
              <option value="13">Nagaland</option>
              <option value="14">Manipur</option>
              <option value="15">Mizoram</option>
              <option value="16">Tripura</option>
              <option value="17">Meghalaya</option>
              <option value="18">Assam</option>
              <option value="19">West Bengal</option>
              <option value="20">Jharkhand</option>
              <option value="21">Odisha</option>
              <option value="22">Chhattisgarh</option>
              <option value="23">Madhya Pradesh</option>
              <option value="24">Gujarat</option>
              <option value="26">Dadra and Nagar Haveli and Daman and Diu</option>
              <option value="27">Maharashtra</option>
              <option value="29">Karnataka</option>
              <option value="30">Goa</option>
              <option value="31">Lakshadweep</option>
              <option value="32">Kerala</option>
              <option value="33">Tamil Nadu</option>
              <option value="34">Puducherry</option>
              <option value="35">Andaman and Nicobar Islands</option>
              <option value="36">Telangana</option>
              <option value="37">Andhra Pradesh</option>
              <option value="38">Ladakh</option>
              <option value="97">Other Territory</option>
            </select>
          </Field>
          <Field label="Pincode">
            <Input value={form.pincode} onChange={e => set('pincode', e.target.value)} placeholder="110001" />
          </Field>
          <Field label="Country">
            <Input value={form.country} onChange={e => set('country', e.target.value)} placeholder="India" />
          </Field>
        </div>
      </div>

      {/* ── Section 3: Financial ── */}
      <div className="crm-card" style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>Financial Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <Field label="Payment Terms (days)">
            <Input value={form.payment_terms_days} onChange={e => set('payment_terms_days', e.target.value)} type="number" min="0" placeholder="e.g. 30" />
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Opening Balance (₹)">
            <Input value={form.opening_balance} onChange={e => set('opening_balance', e.target.value)} type="number" min="0" placeholder="0" />
          </Field>
          <Field label="Opening Balance Type">
            <Select value={form.opening_balance_type} onChange={e => set('opening_balance_type', e.target.value)}>
              <option value="debit">Payable — We owe them (normal)</option>
              <option value="credit">Advance — They owe us (overpayment)</option>
            </Select>
          </Field>
        </div>
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 12, color: '#1d4ed8' }}>
          <strong>Payable (Debit):</strong> You owe this vendor money from before this system started. &nbsp;
          <strong>Advance (Credit):</strong> You overpaid or pre-paid this vendor and they owe you money.
        </div>
      </div>

      {/* ── Section 4: Notes ── */}
      <div className="crm-card" style={{ marginBottom: 20 }}>
        <div style={sectionTitle}>Notes</div>
        <textarea
          style={{ ...inputStyle, minHeight: 90, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }}
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="Internal notes about this vendor…"
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FiSave size={14} /> {saving ? 'Saving…' : 'Save Vendor'}
        </button>
      </div>
    </div>
  );
}